import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BUILDER_CHART_VIEW_TYPES, FavoriteCoin, RANGE_TYPES } from '@/lib/types/hypertrackerTypes';
import {
    BehaviouralData,
    BuilderUser,
    HypertrackerState,
    LEADERBOARDS,
    LeaderboardSpecificTableFilters,
    MeType,
    PerformanceData,
    PositionExplorerFilters,
    SocialData,
    TelegramConnectionHT,
    VaultConnection,
    WalletConnection,
    WalletsParams,
} from '@/state/hypertracker/hypertrackerTypes';

const defaultFilters: LeaderboardSpecificTableFilters = {
    coin: '',
    offset: 0,
    order: 'desc',
    orderBy: '',
    segmentIds: [],
    valueRange: [],
    rankBy: '',
};

const leaderboardFiltersInitialValues = {
    [LEADERBOARDS.PNL_PERP_ONLY]: defaultFilters,
    [LEADERBOARDS.ALL_PNL]: defaultFilters,
    [LEADERBOARDS.AVG_DAILY_PERP_PNL]: defaultFilters,
    [LEADERBOARDS.BIGGEST_POSITIONS]: defaultFilters,
    [LEADERBOARDS.TOP_CLAIMED]: defaultFilters,
    [LEADERBOARDS.REFERRALS]: defaultFilters,
    [LEADERBOARDS.TOP_HYPE_HOLDERS]: defaultFilters,
    [LEADERBOARDS.RICH_LIST]: defaultFilters,
    [LEADERBOARDS.BIGGEST_30D]: defaultFilters,
    [LEADERBOARDS.HIGHEST_EXPOSURE]: defaultFilters,
    [LEADERBOARDS.FAVORITES]: defaultFilters,
    [LEADERBOARDS.MOST_FAVORITED]: defaultFilters,
    [LEADERBOARDS.VAULT_LEADERBOARDS]: defaultFilters,
    limit: 50,
};

const initialState: HypertrackerState = {
    segmentData: null,
    largestPositions: {},
    liquidationRisk: {},
    filters: {
        largestPositions: {
            limit: 50,
            offset: 0,
        },
        wallets: {
            limit: 50,
            offset: 0,
            order: 'desc',
            orderBy: 'openValue',
            pnlSegmentIds: [],
            sizeSegmentIds: [],
            hasOpenPositions: undefined,
        },
        liquidationRisk: {
            limit: 50,
            offset: 0,
        },
    },
    modalWindow: {
        modalType: null,
        isTwitterData: false,
        xUser: null,
        benefitModalType: null,
        authorizationModalType: null,
        wallet: '',
        twitterConnectUrl: '',
        positionId: '',
        editDisplayedNameForFavorites: null,
        vaultInfoModalDetails: null,
    },
    marketRadarActiveTab: 0,
    marketRadarSelectedColor: 'colored',
    marketRadarLimitOrderType: 'all' as 'increase' | 'decrease' | 'all',
    isLoadingDisplayX: false,
    heatmapSelectedRow: null,
    heatmapSelectedField: null,
    tempKeyData: null,
    connections: [],
    vaults: [],
    perpsCurrentAsset: 'BTC',
    perpsPositionsToExplore: [],
    positionExplorerFilters: {
        coin: '',
        limit: 10,
        offset: 0,
        order: 'desc',
        orderBy: 'coin',
        segmentIds: [],
        valueRange: [],
    },
    liquidationHeatmapData: {},
    buildersDailyAnalytics: null,
    buildersAllTimeAnalytics: null,
    buildersAllTime: null,
    builders24h: null,
    topBuildersRevenue: null,
    builderMainChartView: null,
    buildersTableRange: RANGE_TYPES.MONTH,
    personalBuilderRange: null,
    leaderboardsTableFilters: leaderboardFiltersInitialValues,
    buildersForSearch: [],
    positionRecencyToggle: RANGE_TYPES.DAY,
    perpsPositionAge: RANGE_TYPES.ALL,
    leaderboardsPerformanceData: null,
    leaderboardsBehaviouralData: null,
    leaderboardsSocialData: null,
    leaderboards: null,
    me: null,
    telegramConnectionHT: null,
    marketRadarSelectedCoin: '',
    marketRadarSelectedCandle: '5m',
    favoriteCoinsList: [],
    alphafeedSelectedWallet: null,
};

const slice = createSlice({
    name: 'hypertracker',
    initialState,
    reducers: {
        resetHypertracker: (state) => {
            state.filters = {
                largestPositions: {
                    limit: 50,
                    offset: 0,
                },
                wallets: {
                    limit: 50,
                    offset: 0,
                    order: 'desc',
                    orderBy: 'openValue',
                    pnlSegmentIds: [],
                    sizeSegmentIds: [],
                    hasOpenPositions: undefined,
                },
                liquidationRisk: {
                    limit: 50,
                    offset: 0,
                },
            };
            state.positionExplorerFilters = {
                coin: '',
                limit: 10,
                offset: 0,
                order: 'desc',
                orderBy: 'coin',
                segmentIds: [],
                valueRange: [],
            };
            state.connections = [];
            state.vaults = [];
            state.leaderboardsTableFilters = leaderboardFiltersInitialValues;
            state.me = null;
            state.telegramConnectionHT = null;
            state.favoriteCoinsList = [];
        },

        setWalletsFilter: (
            state,
            action: PayloadAction<Partial<Omit<WalletsParams, 'segmentId'>>>,
        ) => {
            state.filters.wallets = {
                ...state.filters.wallets,
                ...action.payload,
            };
        },
        resetWalletsFilter: (state) => {
            state.filters.wallets = {
                ...state.filters.wallets,
                order: 'desc',
                orderBy: 'openValue',
            };
        },
        setModalWindow: (state, action: PayloadAction<any>) => {
            state.modalWindow = {
                ...{
                    modalType: null,
                    isTwitterData: false,
                    xUser: null,
                    benefitModalType: null,
                },
                ...action.payload,
            };
        },
        resetModalWindow: (state) => {
            state.modalWindow = {
                modalType: null,
                isTwitterData: false,
                xUser: null,
                benefitModalType: null,
                authorizationModalType: null,
                wallet: '',
                twitterConnectUrl: '',
                positionId: '',
                editDisplayedNameForFavorites: null,
                vaultInfoModalDetails: null,
                customAddress: '',
            };
        },

        setIsLoadingDisplayX: (state, action: PayloadAction<boolean>) => {
            state.isLoadingDisplayX = action.payload;
        },

        setHeatmapSelectedRow: (state, action: PayloadAction<string | null>) => {
            state.heatmapSelectedRow = action.payload;
        },
        setHeatmapSelectedField: (state, action: PayloadAction<number | null>) => {
            state.heatmapSelectedField = action.payload;
        },
        setTempKeyData: (state, action: PayloadAction<any>) => {
            state.tempKeyData = action.payload;
        },
        setConnections: (state, action: PayloadAction<WalletConnection[]>) => {
            state.connections = action.payload;
        },
        updateSingleConnectionLabel: (state, action: PayloadAction<{ address; label }>) => {
            const { address, label } = action.payload;
            state.connections = state.connections.map((connection) => {
                if (connection.address === address) {
                    return { ...connection, privateName: label };
                }
                return connection;
            });
        },
        updateConnection: (state, action: PayloadAction<WalletConnection>) => {
            const { address } = action.payload;
            state.connections = state.connections.map((connection) => {
                if (connection.address === address) {
                    return { ...action.payload };
                }
                return connection;
            });
        },

        setVaults: (state, action: PayloadAction<VaultConnection[]>) => {
            state.vaults = action.payload;
        },

        setPerpsCurrentAsset: (state, action: PayloadAction<string>) => {
            state.perpsCurrentAsset = action.payload;
        },
        addToPositionsToExplore: (state, action: PayloadAction<string>) => {
            if (state.perpsPositionsToExplore.includes(action.payload)) return;
            state.perpsPositionsToExplore.push(action.payload);
        },
        deleteFromPositionsToExplore: (state, action: PayloadAction<string | number>) => {
            state.perpsPositionsToExplore = state.perpsPositionsToExplore.filter(
                (item) => item !== action.payload,
            );
        },
        clearPositionsToExplore: (state) => {
            state.perpsPositionsToExplore = [];
        },
        setPositionExplorerFilters: (
            state,
            action: PayloadAction<Partial<PositionExplorerFilters>>,
        ) => {
            state.positionExplorerFilters = {
                ...state.positionExplorerFilters,
                ...action.payload,
            };
        },
        setLeaderboardsTableFilters: (
            state,
            action: PayloadAction<{
                filters: Partial<LeaderboardSpecificTableFilters>;
                currentLeaderboard: LEADERBOARDS;
            }>,
        ) => {
            state.leaderboardsTableFilters = {
                ...state.leaderboardsTableFilters,
                [action.payload.currentLeaderboard]: {
                    ...state.leaderboardsTableFilters[action.payload.currentLeaderboard],
                    ...action.payload.filters,
                },
            };
        },
        setBuilderMainChartView: (
            state,
            action: PayloadAction<{ key: string; view: BUILDER_CHART_VIEW_TYPES }>,
        ) => {
            state.builderMainChartView = {
                ...state.builderMainChartView,
                [action.payload.key]: action.payload.view,
            };
        },
        setBuildersTableRange: (state, action: PayloadAction<RANGE_TYPES>) => {
            state.buildersTableRange = action.payload;
        },
        setPersonalBuilderRange: (
            state,
            action: PayloadAction<{ key: string; range: RANGE_TYPES }>,
        ) => {
            state.personalBuilderRange = {
                ...state.personalBuilderRange,
                [action.payload.key]: action.payload.range,
            };
        },

        resetLeaderboardsFilters: (state) => {
            state.leaderboardsTableFilters = leaderboardFiltersInitialValues;
        },
        setBuildersForSearch: (state, action: PayloadAction<BuilderUser[]>) => {
            state.buildersForSearch = action.payload;
        },
        setLeaderboardsLimit: (state, action) => {
            state.leaderboardsTableFilters.limit = action.payload;
        },
        setPositionRecencyToggle: (state, action: PayloadAction<RANGE_TYPES>) => {
            state.positionRecencyToggle = action.payload;
        },
        setPerpsPositionAge: (state, action: PayloadAction<RANGE_TYPES>) => {
            state.perpsPositionAge = action.payload;
        },
        setLeaderboardsPerformanceData: (state, action: PayloadAction<PerformanceData>) => {
            state.leaderboardsPerformanceData = action.payload;
        },
        setLeaderboardsBehaviouralData: (state, action: PayloadAction<BehaviouralData>) => {
            state.leaderboardsBehaviouralData = action.payload;
        },
        setLeaderboardsSocialData: (state, action: PayloadAction<SocialData>) => {
            state.leaderboardsSocialData = action.payload;
        },
        setMe: (state, action: PayloadAction<MeType>) => {
            state.me = action.payload;
        },
        setTelegramConnectionHT: (state, action: PayloadAction<TelegramConnectionHT | null>) => {
            state.telegramConnectionHT = action.payload;
        },
        setMarketRadarActiveTab: (state, action: PayloadAction<number>) => {
            state.marketRadarActiveTab = action.payload;
        },
        setMarketRadarSelectedCoin: (state, action: PayloadAction<string>) => {
            state.marketRadarSelectedCoin = action.payload;
        },
        setMarketRadarSelectedColor: (state, action: PayloadAction<'colored' | 'mono'>) => {
            state.marketRadarSelectedColor = action.payload;
        },
        setMarketRadarLimitOrderType: (
            state,
            action: PayloadAction<'increase' | 'decrease' | 'all'>,
        ) => {
            state.marketRadarLimitOrderType = action.payload;
        },
        setMarketRadarSelectedCandle: (state, action: PayloadAction<string>) => {
            state.marketRadarSelectedCandle = action.payload;
        },
        setFavoriteCoinsList: (state, action: PayloadAction<FavoriteCoin[]>) => {
            state.favoriteCoinsList = action.payload;
        },
        setAlphafeedSelectedWallet: (state, action: PayloadAction<string | null>) => {
            state.alphafeedSelectedWallet = action.payload;
        },
    },
});

export const {
    resetHypertracker,
    setWalletsFilter,
    resetWalletsFilter,
    setModalWindow,
    resetModalWindow,
    setIsLoadingDisplayX,
    setHeatmapSelectedRow,
    setHeatmapSelectedField,
    setTempKeyData,
    setConnections,
    updateConnection,
    updateSingleConnectionLabel,
    setPerpsCurrentAsset,
    addToPositionsToExplore,
    deleteFromPositionsToExplore,
    clearPositionsToExplore,
    setVaults,
    setPositionExplorerFilters,
    setBuilderMainChartView,
    setBuildersTableRange,
    setPersonalBuilderRange,
    setLeaderboardsTableFilters,
    resetLeaderboardsFilters,
    setBuildersForSearch,
    setLeaderboardsLimit,
    setPositionRecencyToggle,
    setPerpsPositionAge,
    setLeaderboardsPerformanceData,
    setLeaderboardsBehaviouralData,
    setLeaderboardsSocialData,
    setMe,
    setTelegramConnectionHT,
    setMarketRadarActiveTab,
    setMarketRadarSelectedCoin,
    setMarketRadarSelectedColor,
    setMarketRadarLimitOrderType,
    setMarketRadarSelectedCandle,
    setFavoriteCoinsList,
    setAlphafeedSelectedWallet,
} = slice.actions;

export default slice.reducer;
